import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import Grid from "../shared/grid"
import VideoProgressBar from "../../design-system/molecules/video-progress-bar"
import useVideoProgressManager from "../shared/hooks/useVideoProgressManager"
import ReactPlayer from "react-player"
import {
  FeatureContent,
  FeatureTitle,
  FeatureText,
} from "../shared/verticals.style"
import FadedTitle from "../shared/faded-title"

import BreakpointImage from "../../design-system/atoms/breakpoint-image"

import {
  LinkContainer,
  CopyText,
  Container,
  ImageContainer,
  VideoProgressContainer,
  featuresImageStyle,
  featuresBodyStyle,
  adContentImageStyle,
  adContentBodyStyle,
  insightsImageStyle,
  insightsBodyStyle,
  buttonStyle,
} from "./feature-highlights.style"

const VideoElementDetails = {
  segment1: {
    title: "Smart Search",
    start: 0,
    end: 10,
  },
  segment2: {
    title: "Quick Launch Buttons",
    start: 10,
    end: 21,
  },
  segment3: {
    title: "Multi-Floor Navigation",
    start: 21,
    end: 38,
  },
  segment4: {
    title: "Accessibility Mode",
    start: 38,
    end: 46,
  },
  segment5: {
    title: "Smart Labels",
    start: 46,
    end: 68,
  },
  segment6: {
    title: "Mobile Pass",
    start: 68,
    end: 79,
  },
}

export default function FeatureHighlights() {
  const query = useStaticQuery(graphql`
    query {
      directoryFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Digital Directory/Desktop/fade_titles/directory_title.svg"
        }
      ) {
        publicURL
      }
      featuresImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Digital Directory/Desktop/directory_video_placehodler.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      adContentImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Digital Directory/Desktop/advertising content.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 90) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      mobileAdContentImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Digital Directory/Desktop/advertising_content_mobile.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 800, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      insightImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Digital Directory/Desktop/insights_analytics.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const { videoPlayerRef, videoProgress, setVideoProgress, seekToTimestamp } =
    useVideoProgressManager()
  
  return (
    <Container>
      <Grid>
        <ImageContainer styles={featuresImageStyle}>
          <ReactPlayer
            url="https://vimeo.com/427470425/1e4d99122b"
            playing={true}
            loop={true}
            volume={0}
            muted={true}
            controls={false}
            onProgress={(data) => setVideoProgress(data)}
            ref={videoPlayerRef}
            playsinline={true}
          />
        </ImageContainer>
        <FeatureContent styleModifier={featuresBodyStyle}>
          <FadedTitle
            title="Digital Directory Features"
            image={query.directoryFade.publicURL}
            alt="The word 'Directory' displayed behind the title"
            fadeSettings={{ lg: "left" }}
          />
          <CopyText>
            With a focus on usability, our Digital Directory caters to visitors
            who know what they’re looking for and those looking to discover
            what’s available.
            <br />
            <br />
            Features such as Smart Search and category listings make finding a
            destination easy. That, paired with the ability to take directions
            on-the-go through Mobile Pass, creates an end-to-end user journey.
          </CopyText>
        </FeatureContent>
        <VideoProgressContainer>
          <VideoProgressBar
            details={VideoElementDetails.segment1}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.segment2}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.segment3}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.segment4}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.segment5}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.segment6}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
        </VideoProgressContainer>

        <ImageContainer styles={adContentImageStyle}>
          <BreakpointImage
            imageVariants={{
              sm: query.mobileAdContentImg.childImageSharp.fluid,
              md: query.mobileAdContentImg.childImageSharp.fluid,
              lg: query.adContentImg.childImageSharp.fluid,
            }}
          />
        </ImageContainer>
        <FeatureContent styleModifier={adContentBodyStyle}>
          <FeatureTitle>Advertising Content</FeatureTitle>
          <FeatureText>
            Through integrations with advertising partners and dedicated content
            placements, the Digital Directory is set up to expose ads, events
            and promotions. With the flexibility to tap into existing
            advertising workflows, or leverage our tools to expose your ads, the
            Digital Directory covers all experiential content needs.
          </FeatureText>
        </FeatureContent>
        <ImageContainer styles={insightsImageStyle}>
          <Img fluid={query.insightImg.childImageSharp.fluid} alt="" />
        </ImageContainer>
        <FeatureContent styleModifier={insightsBodyStyle}>
          <FeatureTitle>Directory Insights & Analytics</FeatureTitle>
          <FeatureText>
            Gain a better understanding of what your visitors are looking for by
            tracking searches and interactions with every part of the Digital
            Directory. Missed searches can indicate locations that visitors are
            looking for but don’t exist in your venue. These insights can lead
            to more informed business decisions about your property.
          </FeatureText>
        </FeatureContent>
        <FeatureContent styleModifier={buttonStyle}>
          <LinkContainer text="Book a demo" />
        </FeatureContent>
      </Grid>
    </Container>
  )
}
